import React, { useContext, memo } from 'react';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import { AppContext } from '@prisjakt/ui-app-context';
import Viewport from '../../../../components/viewport';
import ErrorBoundary from '../../../../components/error-boundary';
import CorePropertiesTable from '../../../../components/core-properties-table';

import StoreCount from './components/store-count';
import PriceSummary from './components/price-summary';
import CategoryRank from './components/category-rank';
import UserSentiments from './components/user-sentiments';

import * as S from './styles';
import {
  CoreProperty,
  IReadableProductInformationProps,
  PropertyString,
} from './types';

const onError = () => null;

const ReadableProductInformation: React.FC<IReadableProductInformationProps> = ({
  product,
}) => {
  const { t, linkTo, market } = useContext(AppContext);

  if (
    !product ||
    !market.modules?.readableProductInfo ||
    !market.modules?.readableProductInfo.enabled
  ) {
    return null;
  }

  const {
    id,
    pathName,
    name,
    priceSummary,
    prices,
    category,
    popularity,
    description,
    coreProperties,
    reviewBadges,
    userReviewSummary,
    brand,
  } = product;

  // Add brand as a custom "fake" core prop
  const brandProp: PropertyString | null =
    brand && category
      ? {
          __typename: 'PropertyString',
          id: 'brand',
          name: t('product', 'property.brand'),
          type: 'string',
          pretty: brand.name,
          prettyVerbose: brand.name,
          prettyTable: brand.name,
          valueId: brand.id.toString(),
          // By adding pathName the property will be linked
          pathName: linkTo('brandCategory', {
            ...category,
            brandId: brand.id,
          }),
          categoryLink: linkTo('brandCategory', {
            ...category,
            brandId: brand.id,
          }),
        }
      : null;

  const brandProperties: CoreProperty[] = brandProp ? [brandProp] : [];

  const modifiedCoreProperties = brandProperties
    .concat(coreProperties?.nodes || [])
    .filter(Boolean)
    .slice(0, 10); // Some products apparently has _a lot_ of core props

  return (
    <ErrorBoundary onError={onError}>
      <Panel
        header={<PanelHeader title={t('product', 'product-info.title')} />}
        data-test-type="product-info"
      >
        {description && (
          <Viewport mobile>
            <S.ProductInfo
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </Viewport>
        )}
        <S.ProductInfo>
          <PriceSummary
            productName={name}
            priceSummary={priceSummary}
            prices={prices}
          />{' '}
          <CategoryRank
            name={name}
            category={category}
            popularity={popularity}
            userReviewSummary={userReviewSummary}
          />{' '}
          <UserSentiments
            id={id}
            pathName={pathName}
            reviewBadges={reviewBadges}
          />{' '}
          <StoreCount name={name} prices={prices} />{' '}
        </S.ProductInfo>
        {coreProperties && (
          <CorePropertiesTable properties={modifiedCoreProperties} />
        )}
      </Panel>
    </ErrorBoundary>
  );
};

export default memo(ReadableProductInformation);
